
import { z } from 'zod';
import { unavailabilityReason } from '../booking/input.ts';
import { DEFAULT_BOOKING_HORIZON_DAYS } from '../booking/slots.ts';

export const isSupportedTimeZone = (tz: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};


export { DEFAULT_BOOKING_HORIZON_DAYS };

export const MIN_BOOKING_HORIZON_DAYS = 2;
export const MAX_BOOKING_HORIZON_DAYS = 30;

// Mutually-exclusive alternative to the rolling horizon: a fixed latest calendar day. Must be
// in the future; end-of-day enforcement happens in the listing's timezone server-side.
export const BookingUntilSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid calendar date.')
  .refine(
    (d) => d > new Date().toISOString().slice(0, 10),
    'The booking-until date must be in the future.',
  );


export const ListingAddressInputSchema = z.object({
  formatted: z.string().trim().min(1, 'An address is required.'),
  line1: z.string().trim().min(1).optional(),
  line2: z.string().trim().min(1).optional(),
  city: z.string().trim().min(1).optional(),
  region: z.string().trim().min(1).optional(),
  postalCode: z.string().trim().min(1).optional(),
  country: z.string().trim().min(1).optional(),
  placeId: z.string().trim().min(1).optional(),
  lat: z.number().min(-90, 'Latitude out of range.').max(90, 'Latitude out of range.').optional(),
  lng: z
    .number()
    .min(-180, 'Longitude out of range.')
    .max(180, 'Longitude out of range.')
    .optional(),
  timezone: z
    .string()
    .trim()
    .min(1, 'A time zone is required.')
    .refine(isSupportedTimeZone, 'Select a valid IANA time zone.'),
});

export type ListingAddressInput = z.infer<typeof ListingAddressInputSchema>;


export const UnavailabilityBlockInputSchema = z
  .object({
    start: z.string().datetime({ offset: true, message: 'Invalid start time.' }),
    end: z.string().datetime({ offset: true, message: 'Invalid end time.' }),
    reason: unavailabilityReason,
  })
  .refine((b) => new Date(b.end).getTime() > new Date(b.start).getTime(), {
    message: 'An unavailability block must end after it starts.',
    path: ['end'],
  });

export type UnavailabilityBlockInput = z.infer<typeof UnavailabilityBlockInputSchema>;


export const BookingAccessKind = {
  AllAgents: 'all_agents',
  SpecificAgents: 'specific_agents',
} as const;
export type BookingAccessKind = (typeof BookingAccessKind)[keyof typeof BookingAccessKind];

export const BookingAccessInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(BookingAccessKind.AllAgents) }),
  z.object({
    kind: z.literal(BookingAccessKind.SpecificAgents),
    userIds: z
      .array(z.string().uuid('Each allowed agent must be a valid user id.'))
      .min(1, 'Select at least one agent, or allow all agents.'),
  }),
]);

export type BookingAccessInput = z.infer<typeof BookingAccessInputSchema>;

export const DEFAULT_BOOKING_ACCESS: BookingAccessInput = { kind: BookingAccessKind.AllAgents };

export const NotificationConfigInputSchema = z.object({
  notifyUserIds: z
    .array(z.string().uuid('Each notify recipient must be a valid user id.'))
    .default([]),
});

export type NotificationConfigInput = z.infer<typeof NotificationConfigInputSchema>;

export const CreateListingInputSchema = z.object({
  address: ListingAddressInputSchema,
  showingNotes: z.string().trim().max(5000, 'Showing notes are too long.').optional(),
  bookingHorizonDays: z
    .number()
    .int('The booking horizon must be a whole number of days.')
    .min(MIN_BOOKING_HORIZON_DAYS, `The booking horizon must be at least ${MIN_BOOKING_HORIZON_DAYS} days.`)
    .max(MAX_BOOKING_HORIZON_DAYS, `The booking horizon cannot exceed ${MAX_BOOKING_HORIZON_DAYS} days.`)
    .default(DEFAULT_BOOKING_HORIZON_DAYS),
  bookingUntil: BookingUntilSchema.optional(),
  unavailability: z.array(UnavailabilityBlockInputSchema).default([]),
  bookingAccess: BookingAccessInputSchema.default(DEFAULT_BOOKING_ACCESS),
  notifyUserIds: z
    .array(z.string().uuid('Each notify recipient must be a valid user id.'))
    .default([]),
});

export type CreateListingInput = z.infer<typeof CreateListingInputSchema>;

export const UpdateListingInputSchema = z.object({
  address: ListingAddressInputSchema.optional(),
  showingNotes: z.string().trim().max(5000, 'Showing notes are too long.').nullable().optional(),
  bookingHorizonDays: z
    .number()
    .int('The booking horizon must be a whole number of days.')
    .min(MIN_BOOKING_HORIZON_DAYS, `The booking horizon must be at least ${MIN_BOOKING_HORIZON_DAYS} days.`)
    .max(MAX_BOOKING_HORIZON_DAYS, `The booking horizon cannot exceed ${MAX_BOOKING_HORIZON_DAYS} days.`)
    .optional(),
  // `null` explicitly clears a previously-set fixed date, reverting to the rolling horizon.
  bookingUntil: BookingUntilSchema.nullable().optional(),
  unavailability: z.array(UnavailabilityBlockInputSchema).optional(),
  bookingAccess: BookingAccessInputSchema.optional(),
  notifyUserIds: z.array(z.string().uuid('Each notify recipient must be a valid user id.')).optional(),
});

export type UpdateListingInput = z.infer<typeof UpdateListingInputSchema>;
